import React, {useCallback, useMemo, useState} from "react";

import { Ghost, Secondary } from "@churchofjesuschrist/eden-buttons";
import { DialogModal } from "@churchofjesuschrist/eden-dialog-modal";
import {
  FormField,
  Input,
  Label,
  Select
} from "@churchofjesuschrist/eden-form-parts";
import { Edit } from "@churchofjesuschrist/eden-icons";
import { Stack } from "@churchofjesuschrist/eden-stack";
import { spacing16,
  spacing64,
  red40,
  green40,
  grey40,
  grey35,
  blue40 } from "@churchofjesuschrist/eden-style-constants";
import {
  BodyRail,
  ImageRail,
  VerticalModularTile,
} from "@churchofjesuschrist/eden-vertical-modular-tile";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";

import stringHasValue from "../../../utils/string-has-value";
import useTranslation from "../../../utils/use-translations";
import ContentPageFooter from "../content-page-footer/content-page-footer";
import documentService from "../document-upload/document-service";
import { SingleDocumentUpload } from "../document-upload/document-upload";
import GradiantTopBar from "../gradiant-top-bar/gradiant-top-bar";
import useTitanApi from "../hooks/use-titan-api/use-titan-api";

const styledColors = {
  red40, green40, grey40, grey35, blue40
};

const StyledCard = styled(VerticalModularTile)`
  text-align: center;
  padding-bottom:${({preview}) => preview ? 0 : spacing64};
  height: 100%;
  position:relative;
`;

const CardBody = styled(BodyRail)`
  width: 100%;
`;

const CallToAction = styled.div`
  text-align: center;
  width: 100%;
  position:absolute;
  bottom:${spacing16};
`;

const RailHolder = styled.div`
background: ${({color}) => styledColors[color]};
img{
  visibility:visible !important;
}
`;


const stringGroups = [
  {
    name: "admin",
    keys: [
      "title",
      "details",
      "url",
      "buttonText",
      "color",
      "red40",
      "green40",
      "grey40",
      "blue40",
      "image",
      "imageHelp"
    ]
  },
  {
    name: "shared",
    keys: [
      "ok",
      "remove"
    ]
  }
]

const defaultService = documentService();

const defaultObj = {};
const ContentCard = ({
  service = defaultService,
  image = defaultObj,//image is an object we can pass to useTitanApi to get our url
  imageUrl, //sometimes we have a url to an image where we aren't using titan (like the pathway logo)
  title,
  color,
  body,
  children,
  callToActionHref,
  callToActionRoute,
  callToActionText,
  target,
  preview = false,
  dataId,
  index,
  onChangeCard,
}) => {
  const navigate = useNavigate();
  const { managedStrings } = useTranslation(stringGroups);

  const [editing, setEditing] = useState(false);
  const onEdit = useCallback(() => setEditing(true), []);
  const onCancel = useCallback(() => setEditing(false), []);
  const onChange = useCallback((e) => onChangeCard(index, e), [index, onChangeCard]);
  const onFileChange = useCallback((value) => {
    onChangeCard(index, { target: { name: "imageUrl", value } });
  }, [index, onChangeCard]);

  const titanUrl = useTitanApi(image);
  const url = stringHasValue(imageUrl) ? imageUrl : titanUrl;
  const buttonProps = useMemo(() => {
    return {
      href: !stringHasValue(callToActionRoute) ? callToActionHref : undefined,
      onClick: stringHasValue(callToActionRoute) ? () => {
        navigate(callToActionRoute)
      } : undefined,
      target
    }
  }, [callToActionHref, callToActionRoute, navigate, target]);
  return (
    <>
      <StyledCard data-id={dataId} preview={preview}>
        <Stack>
          {!preview && <div>
            <GradiantTopBar color={color} />
            <RailHolder color={color}>
              {url && <ImageRail src={url}
                alt={title}
                aspectRatio="8:5"/>}
            </RailHolder>
          </div>}
          <CardBody
            title={title}
          >
            {body}
            {children}
          </CardBody>

          {!preview && (callToActionHref || callToActionRoute) &&
                  callToActionText && (<CallToAction>
            <Secondary {...buttonProps}>{callToActionText}</Secondary>
          </CallToAction>)
          }

        </Stack>
      </StyledCard>
      {onChangeCard && <Ghost small={true}
        onClick={onEdit}>
        <Edit />
      </Ghost>}
      {editing && <DialogModal open={true} onClose={onCancel}>
        <FormField>
          <Label>
            {managedStrings.color}
          </Label>
          <Select
            name="color"
            value={color}
            onChange={onChange}
          >
            <option value="red40">{managedStrings.red40}</option>
            <option value="green40">{managedStrings.green40}</option>
            <option value="grey40">{managedStrings.grey40}</option>
            <option value="blue40">{managedStrings.blue40}</option>
          </Select>
        </FormField>
        <FormField>
          <Label>
            {managedStrings.image}
          </Label>
          <SingleDocumentUpload
            removeText={managedStrings.remove}
            onFileChange={onFileChange}
            service={service}
          />
          {managedStrings.imageHelp}
        </FormField>
        <FormField>
          <Label>
            {managedStrings.title}
          </Label>
          <Input
            name="title"
            value={title}
            maxLength={50}
            onChange={onChange}
          />
        </FormField>
        <FormField>
          <Label>
            {managedStrings.details}
          </Label>
          <Input
            name="body"
            value={body}
            maxLength={150}
            onChange={onChange}
          />
        </FormField>
        <FormField>
          <Label>
            {managedStrings.url}
          </Label>
          <Input
            name="callToActionHref"
            value={callToActionHref}
            onChange={onChange}
          />
        </FormField>
        <FormField>
          <Label>
            {managedStrings.buttonText}
          </Label>
          <Input
            name="callToActionText"
            value={callToActionText}
            maxLength={30}
            onChange={onChange}
          />
        </FormField>
        <ContentPageFooter canEdit={true}
          mode="active"
          showCancel={false}
          saveText={managedStrings.ok}
          onSave={onCancel} />
      </DialogModal>}
    </>
  );
}

export default ContentCard;
